export type DebouncedThreadRefetchOptions = {
  debounceMs?: number;
  refetchDelayMs?: number;
  refetchDelayOnThreadChangeMs?: number;
};

type PendingRefetch = {
  threadId: string;
  threadIdChanged: boolean;
};

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    /Android|iPhone|iPad/i.test(navigator.userAgent) ||
    window.innerWidth <= 600
  );
}

/**
 * Debounced scheduler for `GET /api/thread/{id}` after WS events.
 * Mirrors legacy widget delays (100–300 ms).
 */
export function createDebouncedThreadRefetch(
  onRefetch: (threadId: string, threadIdChanged: boolean) => void | Promise<void>,
  options?: DebouncedThreadRefetchOptions,
): {
  schedule: (threadId: string, threadIdChanged: boolean) => void;
  cancel: () => void;
} {
  const mobile = isMobileDevice();
  const debounceMs = options?.debounceMs ?? (mobile ? 200 : 100);
  const refetchDelayMs =
    options?.refetchDelayMs ?? (mobile ? 150 : 50);
  const refetchDelayOnThreadChangeMs =
    options?.refetchDelayOnThreadChangeMs ?? (mobile ? 300 : 150);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let refetchTimer: ReturnType<typeof setTimeout> | null = null;
  let pending: PendingRefetch | null = null;

  const cancel = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (refetchTimer) clearTimeout(refetchTimer);
    debounceTimer = null;
    refetchTimer = null;
    pending = null;
  };

  const schedule = (threadId: string, threadIdChanged: boolean) => {
    pending = { threadId, threadIdChanged };
    if (debounceTimer) clearTimeout(debounceTimer);

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const job = pending;
      pending = null;
      if (!job) return;

      const delay = job.threadIdChanged
        ? refetchDelayOnThreadChangeMs
        : refetchDelayMs;

      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        void onRefetch(job.threadId, job.threadIdChanged);
      }, delay);
    }, debounceMs);
  };

  return { schedule, cancel };
}
